import {
  ChevronDown,
  Filter,
  Info,
  Users,
  Star,
  Cake,
  Sparkles,
  UserPlus,
  Clock,
  Calendar,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { ComponentType } from 'react';
import type { CrmSegment, CrmSubFilter } from '@/types/crm';
import { ScrollableContainer } from '@/components/common/ScrollableContainer';

export interface SegmentSelectorProps {
  segments: CrmSegment[];
  selectedSegmentId: string;
  onSelectSegment: (id: string) => void;
  activeSubFilter: CrmSubFilter;
  onSelectSubFilter: (subFilter: CrmSubFilter) => void;
  loading?: boolean;
  subFilterCounts?: Record<CrmSubFilter, { pending: number; total: number }>;
  segmentCounts?: Record<string, { pending: number; total: number }>;
}

interface QuickChipItem {
  id: CrmSubFilter;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
}

const QUICK_SUBFILTER_CHIPS: QuickChipItem[] = [
  { id: 'all', label: 'Todos', icon: Users },
  { id: 'vip', label: 'Clientes fieles', icon: Star },
  { id: 'birthday', label: 'Cumpleaños', icon: Cake },
  { id: 'top_spender', label: 'Mayor Consumo', icon: Sparkles },
  { id: 'new', label: 'Nuevos', icon: UserPlus },
  { id: 'lapsed', label: 'Inactivos', icon: Clock },
  { id: 'debt', label: 'Con deuda', icon: AlertTriangle },
  { id: 'with_appointments', label: 'Con citas', icon: Calendar },
];

export function SegmentSelector({
  segments,
  selectedSegmentId,
  onSelectSegment,
  activeSubFilter,
  onSelectSubFilter,
  loading,
  subFilterCounts,
  segmentCounts,
}: SegmentSelectorProps) {
  const activeSegment = segments.find((s) => s.id === selectedSegmentId);
  const allSegCount = segmentCounts?.[''];
  const formatCountBadge = (pending: number, total: number) => {
    if (total === 0) return '(0)';
    if (pending === total) return `(${total})`;
    return `(${pending}/${total})`;
  };

  return (
    <div className="space-y-2.5">
      {/* Detailed Native Segment Dropdown (Primary Category Filter) */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none">
          <Filter size={14} />
        </div>
        <select
          value={selectedSegmentId}
          onChange={(e) => onSelectSegment(e.target.value)}
          disabled={loading}
          className="w-full appearance-none bg-surface-secondary border border-border rounded-xl pl-9 pr-9 py-2 text-xs font-medium text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary cursor-pointer disabled:opacity-50 shadow-2xs min-h-[36px]"
        >
          <option value="">
            Directorio Completo (Todos los clientes)
            {allSegCount ? ` ${formatCountBadge(allSegCount.pending, allSegCount.total)}` : ''}
          </option>
          {segments.map((seg) => {
            const cnt = segmentCounts?.[seg.id];
            return (
              <option key={seg.id} value={seg.id} className="bg-surface text-text-primary">
                {seg.name}
                {cnt ? ` ${formatCountBadge(cnt.pending, cnt.total)}` : ''}
                {seg.description ? ` — ${seg.description}` : ''}
              </option>
            );
          })}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
        />
      </div>

      {/* Top Horizontal Scrollable Quick Subfilter Pills */}
      <ScrollableContainer
        scrollClassName="gap-1.5 py-0.5"
        className="-mx-1 px-1"
        scrollAmount={180}
      >
        {QUICK_SUBFILTER_CHIPS.map((chip) => {
          const isActive = activeSubFilter === chip.id;
          const cnt = subFilterCounts?.[chip.id];
          const isDone = cnt && cnt.total > 0 && cnt.pending === 0;
          const Icon = chip.icon;

          return (
            <button
              key={chip.id}
              type="button"
              disabled={loading}
              onClick={() => onSelectSubFilter(chip.id)}
              className={clsx(
                'whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all shrink-0 cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5 min-h-[36px]',
                isActive
                  ? 'bg-brand-primary text-white shadow-xs font-semibold'
                  : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary hover:text-text-primary border border-border'
              )}
            >
              <Icon
                size={12}
                className={clsx('shrink-0', isActive ? 'text-white' : 'text-brand-primary')}
              />
              <span>{chip.label}</span>
              {cnt !== undefined && (
                <span
                  className={clsx(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center gap-0.5',
                    isActive
                      ? isDone
                        ? 'bg-emerald-500/30 text-emerald-100'
                        : 'bg-white/20 text-white'
                      : isDone
                      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
                      : 'bg-surface-tertiary text-text-muted border border-border/60'
                  )}
                >
                  {isDone && <Check size={10} className="shrink-0 font-bold" />}
                  <span>
                    {cnt.pending}
                    {cnt.pending !== cnt.total ? `/${cnt.total}` : ''}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </ScrollableContainer>

      {/* Active Segment Description Hint */}
      {activeSegment?.description && (
        <div className="flex items-start gap-2 p-2.5 bg-surface-secondary/70 border border-border rounded-xl text-[11px] text-text-secondary animate-fade-in">
          <Info size={14} className="text-brand-primary shrink-0 mt-0.5" />
          <span className="leading-relaxed">{activeSegment.description}</span>
        </div>
      )}
    </div>
  );
}
